// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * RESEARCH §1's provider matrix, as data. One `KeywordRule` per row.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * LEGEND CAVEAT — MANDATORY IN THE UI.
 *
 * `SUP` means "on the target's documented supported list". It does NOT mean "enforced at decode".
 * **OpenAI never uses the word "enforced" anywhere in structured-outputs.md** — every keyword carries
 * only a descriptive gloss ("minimum — The number must be greater than or equal to this value").
 * Rendering a green "enforced" badge on a doc gloss is exactly the confident-and-wrong this tool
 * exists to prevent. Same for Gemini: a field's presence in the discovery `Schema` message proves the
 * request will not 400, and proves nothing at all about enforcement.
 *
 * `UNKNOWN` is a first-class value and is used liberally. Several keywords are documented by nobody.
 * The honest cell is "the provider documents neither support nor rejection for this keyword"; every
 * `UNKNOWN` cell must have a matching entry in `./unresolved.ts` (tests/targets.test.ts enforces it).
 * Never fill a cell to make the table look complete.
 *
 * Provider error strings are copied byte-for-byte from RESEARCH §1. Never paraphrase a 400.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * KEYWORD NAMING. A row's `keyword` is the JSON Schema keyword a linter looks up when it walks a
 * schema. Rows that describe a *variant* of a keyword rather than the keyword itself carry a `#`
 * suffix and are never produced by a plain keyword lookup:
 *
 *   type#union                     `{"type":["string","null"]}` rather than a scalar type
 *   additionalProperties#non-false `true`, `{}` or a subschema rather than the literal `false`
 *   $ref#root / #external / #siblings   `"#"` self-recursion · an `https://` target · sibling keys
 *   required#ghost                 a `required` entry naming a key absent from `properties`
 *   #root-object                   the schema root itself, which is not a keyword at all
 */

import type { Cell, KeywordRule, Provenance, Status, TargetId } from '../types.ts';

interface Extra {
  readonly note?: string;
  readonly providerError?: string;
}

function cell(status: Status, provenance: Provenance, extra: Extra = {}): Cell {
  return { status, provenance, ...extra };
}

const REQ = (p: Provenance, x?: Extra): Cell => cell('REQ', p, x);
const SUP = (p: Provenance, x?: Extra): Cell => cell('SUP', p, x);
const ACC = (p: Provenance, x?: Extra): Cell => cell('ACC', p, x);
const NO = (p: Provenance, x?: Extra): Cell => cell('REJECT', p, x);
const DROP = (p: Provenance, x?: Extra): Cell => cell('DROP', p, x);
const UNK = (x?: Extra): Cell => cell('UNKNOWN', 'INFERRED', x);

/** Chat Completions non-strict is advisory end to end: the same cell for every row. */
const CHAT: Cell = ACC('DOC', {
  note: 'Chat Completions is non-strict by default — the schema is a prompt hint and nothing is validated. The keyword is accepted here and may still 400 the moment you set strict:true or move to Responses.',
});

/** GBNF handling is keyword-identical across llama.cpp and xgrammar; only the emission differs (§4). */
function gbnf(c: Cell): Partial<Record<TargetId, Cell>> {
  return { 'gbnf-llamacpp': c, 'gbnf-xgrammar': c };
}

/** Anthropic's documented silence, reused verbatim. */
const ANT_SILENT = (): Cell =>
  UNK({ note: 'Named on neither of Anthropic\'s lists — Anthropic documents neither support nor rejection for this keyword.' });

// ── Literal provider errors (RESEARCH §1, byte-for-byte) ───────────────────────────────────────────

export const ERR_OPENAI_ADDITIONAL_PROPERTIES =
  "Invalid schema for function 'NAME': In context=(), 'additionalProperties' is required to be supplied and to be false.";

export const ERR_OPENAI_REQUIRED =
  "Invalid schema for function 'shell': In context=(), 'required' is required to be supplied and to be an array including every key in properties. Missing 'workdir'.";

export const ERR_OPENAI_ROOT =
  "Invalid schema for function 'browser_click': schema must have type 'object' and not have 'oneOf'/'anyOf'/'allOf'/'enum'/'const'/'not' at the top level.";

export const ERR_OPENAI_UNIQUE_ITEMS = "In context=('properties', 'ids'), 'uniqueItems' is not permitted.";

export const ERR_OPENAI_DEFAULT = "'default' is not permitted.";

export const ERR_ANTHROPIC_TOP_LEVEL_COMPOSITION =
  'input_schema does not support oneOf, allOf, or anyOf at the top level';

export const ERR_GEMINI_ADDITIONAL_PROPERTIES =
  'Invalid JSON payload received. Unknown name "additionalProperties" at \'tools[0].function_declarations[0].parameters\': Cannot find field.';

export const ERR_GEMINI_PROPERTY_NAMES =
  'Invalid JSON payload received. Unknown name "propertyNames" at \'tools[0].function_declarations[1].parameters.properties[1].value.properties[3].value.properties[3].value.any_of[0]\': Cannot find field.';

export const ERR_GEMINI_SCHEMA_KEYWORD =
  'Unknown name "$schema" at \'tools[0].function_declarations[0].parameters\'';

export const ERR_GEMINI_REF = 'Unknown name "ref"';

/**
 * OpenAI returns four distinct error families, not three. The `param` path is API-specific:
 * Chat Completions `tools[0].function.parameters`; Responses `tools[N].parameters`,
 * `tools[N].tools[M].parameters` inside a namespace, or `input[N].tools[...]...` on
 * conversation-carried tool sets. **There is no `.function.` segment on Responses.**
 */
export const OPENAI_ERROR_FAMILIES: readonly {
  readonly family: number;
  readonly template: string;
  readonly provenance: Provenance;
}[] = [
  { family: 1, template: ERR_OPENAI_ADDITIONAL_PROPERTIES, provenance: 'FIELD' },
  { family: 2, template: ERR_OPENAI_REQUIRED, provenance: 'FIELD' },
  {
    family: 3,
    template:
      "Invalid schema for function 'NAME': In context=('properties', 'FIELD'[, 'items']), 'KEYWORD' is not permitted.",
    provenance: 'FIELD',
  },
  { family: 4, template: ERR_OPENAI_ROOT, provenance: 'FIELD' },
];

/** The error object carries these two fields on every family. */
export const OPENAI_ERROR_ENVELOPE = {
  type: 'invalid_request_error',
  code: 'invalid_function_parameters',
} as const;

export const OPENAI_ERROR_PARAM_PATHS: readonly { readonly api: string; readonly path: string }[] = [
  { api: 'chat-completions', path: 'tools[0].function.parameters' },
  { api: 'responses', path: 'tools[N].parameters' },
  { api: 'responses (inside a type:"namespace" group)', path: 'tools[9].tools[4].parameters' },
  { api: 'responses (conversation-carried tool set)', path: 'input[N].tools[...]...' },
];

/**
 * Anthropic strict `pattern` sub-subset (DOC, structured-outputs.md accordion "Pattern support (regex)").
 * Lint the CONTENT of every `pattern`, not just its presence: a belt full of `\b` or lookahead is
 * legal JSON Schema, legal on OpenAI strict, and a 400 on Anthropic strict.
 */
export const ANTHROPIC_PATTERN_SUPPORT = {
  supported: [
    'full `^…$` and partial matching',
    'quantifiers `*` `+` `?` and simple `{n,m}`',
    'character classes `[]` `.` `\\d` `\\w` `\\s`',
    'groups `(...)`',
  ],
  unsupported: [
    { construct: 'backreference', probe: /\\[1-9]/, label: '\\1, \\2 — backreferences' },
    {
      construct: 'lookaround',
      probe: /\(\?<?[=!]/,
      label: '(?=…), (?!…), (?<=…), (?<!…) — lookahead/lookbehind',
    },
    { construct: 'word-boundary', probe: /\\[bB]/, label: '\\b, \\B — word boundaries' },
    { construct: 'large-range-quantifier', probe: /\{\d{3,},?\d*\}/, label: 'complex {n,m} with large ranges' },
  ],
  closingQuote: 'Simple regex patterns work well. Complex patterns may result in 400 errors.',
} as const;

/**
 * OpenAI strict `format` accepts exactly nine values; Anthropic strict accepts ten — the nine plus `uri`.
 * llama.cpp constrains only four.
 */
export const FORMAT_VALUES = {
  'openai-strict': ['date-time', 'time', 'date', 'duration', 'email', 'hostname', 'ipv4', 'ipv6', 'uuid'],
  'anthropic-strict': [
    'date-time',
    'time',
    'date',
    'duration',
    'email',
    'hostname',
    'ipv4',
    'ipv6',
    'uuid',
    'uri',
  ],
  'gbnf-llamacpp': ['date', 'time', 'date-time', 'uuid'],
} as const;

// ── The matrix ─────────────────────────────────────────────────────────────────────────────────────

/**
 * `anthropic-sdk` and `gbnf-xgrammar` are derived where a row does not state them explicitly:
 * the SDK path is the strict path plus client-side rewrites, and xgrammar differs from llama.cpp
 * only in emission. `finalise()` fills them in below; the copy carries the same provenance.
 */
const ROWS: readonly KeywordRule[] = [
  {
    keyword: 'type',
    gloss: 'the JSON type of this node',
    cells: {
      'openai-strict': REQ('DOC', { note: 'Every schema node must carry a type under strict mode.' }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC'),
      'gemini-parameters': REQ('DOC', {
        note: 'Emit the UPPERCASE proto enum name (STRING/NUMBER/INTEGER/BOOLEAN/ARRAY/OBJECT/NULL). The discovery doc says "Required. Data type." — but proto3 has no required fields, the first enum member is TYPE_UNSPECIFIED, and a canonical anyOf node carries no parent type, so enforcement is untested. Whether lowercase is also tolerated is untested — do not claim either way.',
      }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC', {
        note: 'Vertex\'s own discovery document calls this "Optional. Data type of the schema field." — the Gemini API\'s says "Required. Data type." RESEARCH §1 lists Vertex as REQ; the document disagrees and the document wins.',
      }),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: 'type#union',
    gloss: 'an array of types, e.g. ["string","null"] — the nullable-optional idiom',
    cells: {
      'openai-strict': REQ('DOC', {
        note: 'Required for every field you want to be optional, because strict mode forces `required` to list every key. This is human guidance from function-calling.md — OpenAI\'s own Agents SDK never inserts "null", it only rewrites `required`.',
      }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC', {
        note: 'Counts toward the request-wide 16 union-typed parameter cap, exactly as anyOf does.',
      }),
      'gemini-parameters': NO('DOC', {
        note: '`type` is a scalar proto enum, not an array. Rewrite to `{type:STRING, nullable:true}` or to an anyOf. This is the single shape Gemini parameters cannot represent — which is why you must never transpile OpenAI-strict → Gemini.',
      }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(SUP('SRC', { note: 'Expands to a union of copies of the node, one per member type.' })),
    },
  },
  {
    keyword: '#root-object',
    gloss: 'the schema root itself — must be a plain object, not a union or a scalar',
    cells: {
      'openai-strict': REQ('FIELD', {
        providerError: ERR_OPENAI_ROOT,
        note: 'The root error has a different grammar entirely — no context tuple. A single-element list ["object"] is accepted and normalised to "object"; a nullable root ["object","null"] is not.',
      }),
      'openai-nonstrict-responses': REQ('FIELD', { providerError: ERR_OPENAI_ROOT }),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': REQ('DOC', {
        note: '`input_schema.type` is pinned to the literal "object". Whether a non-object input_schema is rejected at runtime is not established.',
      }),
      'anthropic-strict': REQ('DOC'),
      'gemini-parameters': REQ('DOC'),
      'gemini-jsonschema': REQ('DOC', { note: 'The field\'s own description: it "must describe an object".' }),
      'vertex-parameters': REQ('DOC'),
    },
  },
  {
    keyword: 'properties',
    gloss: 'the named members of an object',
    cells: {
      'openai-strict': SUP('DOC', { note: 'Counted against the 5000-property and 120,000-character budgets.' }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC'),
      'gemini-parameters': SUP('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(SUP('SRC', {
        note: 'The grammar hard-locks key order to the DECLARATION order of `properties` — not the order of `required`.',
      })),
    },
  },
  {
    keyword: 'required',
    gloss: 'which properties must be present',
    cells: {
      'openai-strict': REQ('FIELD', {
        providerError: ERR_OPENAI_REQUIRED,
        note: 'Must be supplied and must list EVERY key of `properties`. Widen anything genuinely optional to ["T","null"] instead.',
      }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC', {
        note: 'NOT all keys — optional properties are legal here, and that is Anthropic\'s real advantage over OpenAI strict. But every property absent from `required` counts toward the request-wide 24-optional-parameter cap.',
      }),
      'gemini-parameters': SUP('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: 'required#ghost',
    gloss: 'a `required` entry naming a key that is absent from `properties`',
    cells: {
      'openai-strict': NO('INFERRED', {
        note: 'Implied by the family-2 error, which requires `required` to be an array including every key in properties. No capture of the reverse case exists.',
      }),
      'openai-nonstrict-responses': UNK(),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': UNK(),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': UNK(),
      ...gbnf(DROP('SRC', { note: 'The ghost entry silently vanishes from the grammar.' })),
    },
  },
  {
    keyword: 'additionalProperties',
    gloss: 'whether unlisted properties are allowed — this row is the literal value `false`',
    cells: {
      'openai-strict': REQ('FIELD', {
        providerError: ERR_OPENAI_ADDITIONAL_PROPERTIES,
        note: 'Required at EVERY object level. Compare with `!== false`, never truthiness: `{}` is falsy in JS and a naive linter passes it.',
      }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': REQ('DOC', { note: 'It may be set to nothing other than false.' }),
      'anthropic-sdk': SUP('DOC', {
        note: 'The SDK adds `additionalProperties:false` to all objects for you before sending.',
      }),
      'gemini-parameters': NO('DOC', {
        providerError: ERR_GEMINI_ADDITIONAL_PROPERTIES,
        note: 'THE headline of this whole problem space: `additionalProperties:false` is REQUIRED by OpenAI strict, REQUIRED by Anthropic strict, and does not exist in Gemini\'s Schema message at all. The most-Googled repair here is itself a Gemini-breaking change.',
      }),
      'gemini-jsonschema': ACC('DOC', {
        note: 'Appears in the field\'s own documentation example. Nothing more is published.',
      }),
      'vertex-parameters': SUP('DOC', {
        note: 'Present in Vertex\'s 25-field Schema, absent from the Gemini API\'s 22-field one.',
      }),
      ...gbnf(SUP('SRC', { note: 'Absent ≡ false in llama.cpp — the opposite of JSON Schema\'s default.' })),
    },
  },
  {
    keyword: 'additionalProperties#non-false',
    gloss: '`additionalProperties` set to true, `{}`, or a subschema',
    cells: {
      'openai-strict': NO('SDK', {
        note: 'And `{}` is invalid too — the falsy trap. The MCP/OpenAPI habit of emitting `additionalProperties:{}` produces a schema a naive linter green-lights and the API rejects.',
      }),
      'openai-nonstrict-responses': NO('SDK'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': NO('DOC', {
        note: '"additionalProperties set to anything other than false" is one of Anthropic\'s seven documented unsupported features.',
      }),
      'anthropic-sdk': DROP('DOC', {
        note: 'The SDK overwrites it with false before sending. Your permissive object silently becomes a closed one.',
      }),
      'gemini-parameters': NO('DOC', { providerError: ERR_GEMINI_ADDITIONAL_PROPERTIES }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(SUP('SRC', { note: 'Emits a "not one of the declared strings" key trie.' })),
    },
  },
  {
    keyword: '$defs',
    gloss: 'a container of reusable subschemas',
    cells: {
      'openai-strict': SUP('DOC'),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC', { note: 'Internal references only.' }),
      'gemini-parameters': NO('DOC', {
        note: 'No `defs` field exists in the 22-field Schema message; the key 400s as `Unknown name`.',
      }),
      'gemini-jsonschema': UNK({ note: 'Expected to work, unverified — Google publishes no keyword list for this field.' }),
      'vertex-parameters': SUP('DOC', { note: 'Spelled `defs` (no `$`), root level only.' }),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: 'definitions',
    gloss: 'the draft-07 spelling of $defs',
    cells: {
      'openai-strict': SUP('DOC'),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC'),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC', { note: 'Vertex spells it `defs`; the draft-07 `definitions` is not a field.' }),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: '$ref',
    gloss: 'a reference to another subschema',
    cells: {
      'openai-strict': SUP('DOC', { note: 'Internal `#/$defs/x` form. Do not eagerly expand — that is how `{"$ref":"#"}` fails to terminate.' }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC'),
      'gemini-parameters': NO('DOC', { providerError: ERR_GEMINI_REF, note: 'Refs must be inlined — and cannot be if they are recursive, in which case the tool must be refused.' }),
      'gemini-jsonschema': UNK({ note: 'Expected to work, unverified. Do not tell a user their recursive $ref is safe here.' }),
      'vertex-parameters': SUP('DOC', { note: 'Spelled `ref` (no `$`).' }),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: '$ref#root',
    gloss: '`{"$ref":"#"}` — a schema that references itself',
    cells: {
      'openai-strict': SUP('DOC', {
        note: 'OpenAI\'s own documented recursive example. Do NOT reject it: the SDK\'s "Unexpected $ref format" error is reachable only from the sibling-key unravel path.',
      }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': NO('DOC'),
      'anthropic-strict': NO('DOC', { note: 'Recursive schemas are the first of Anthropic\'s seven documented unsupported features.' }),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': UNK(),
      ...gbnf(UNK({
        note: 'llama.cpp resolves `#/…` refs; a bare `#` is not covered by any read of the source.',
      })),
    },
  },
  {
    keyword: '$ref#external',
    gloss: 'an `https://` or otherwise remote reference target',
    cells: {
      'openai-strict': NO('SDK', { note: 'Only refs starting with `#` are accepted.' }),
      'openai-nonstrict-responses': NO('SDK'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': NO('DOC'),
      'anthropic-strict': NO('DOC', { note: 'External $ref is on Anthropic\'s seven-bullet unsupported list.' }),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': UNK(),
      ...gbnf(NO('SRC', {
        note: 'HARD ERROR. Both construction sites pass a stub fetcher, so the C++ every user actually runs cannot fetch remote refs — its own CLI help tells you to use the Python script instead. Do not implement remote fetching: it would make this tool disagree with llama-server.',
      })),
    },
  },
  {
    keyword: '$ref#siblings',
    gloss: 'a `$ref` carrying sibling keywords alongside it',
    cells: {
      'openai-strict': SUP('SDK', {
        note: 'Must be unravelled before sending: resolved keys first, local keys win, pop the `$ref`, then re-run the strict pass so chained refs survive.',
      }),
      'openai-nonstrict-responses': SUP('SDK'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC', { note: '`ref` is not a field here at all, so the question does not arise.' }),
      'gemini-jsonschema': UNK({
        note: 'A sibling field\'s docs say no properties except those starting with `$` may sit on a `$ref` subschema — but that list belongs to a different, deprecated message.',
      }),
      'vertex-parameters': UNK(),
      ...gbnf(DROP('SRC', { note: 'Siblings are silently ignored — draft-07 behaviour.' })),
    },
  },
  {
    keyword: '$dynamicRef',
    gloss: '2020-12 dynamic reference resolution',
    cells: {
      'openai-strict': UNK(),
      'openai-nonstrict-responses': UNK(),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': UNK(),
      ...gbnf(DROP('SRC')),
    },
  },
  {
    keyword: '$dynamicAnchor',
    gloss: 'the 2020-12 anchor a $dynamicRef resolves against',
    cells: {
      'openai-strict': UNK(),
      'openai-nonstrict-responses': UNK(),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': UNK(),
      ...gbnf(DROP('SRC')),
    },
  },
  {
    keyword: 'anyOf',
    gloss: 'the instance must match at least one subschema',
    cells: {
      'openai-strict': SUP('DOC', { note: 'Supported — but not at the root.' }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': NO('DOC', {
        providerError: ERR_ANTHROPIC_TOP_LEVEL_COMPOSITION,
        note: 'Rejected at the TOP LEVEL of input_schema only. Nested anyOf is a different question.',
      }),
      'anthropic-strict': SUP('DOC', {
        note: 'Counts toward the request-wide 16-union cap: Anthropic calls these "especially expensive because they create exponential compilation cost".',
      }),
      'gemini-parameters': SUP('DOC'),
      'gemini-jsonschema': UNK({ note: 'Expected, unverified.' }),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: 'oneOf',
    gloss: 'the instance must match exactly one subschema',
    cells: {
      'openai-strict': NO('FIELD', {
        note: 'Rejected via the family-3 "is not permitted" error — and rejected on Responses even when `strict` is omitted, which is why "just turn strict off" is never the advice.',
      }),
      'openai-nonstrict-responses': NO('FIELD', {
        note: 'Verbatim from the capture: "The Responses API rejects oneOf even when strict is omitted." One bad tool 400s the entire tools array.',
      }),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': NO('DOC', { providerError: ERR_ANTHROPIC_TOP_LEVEL_COMPOSITION, note: 'At the top level of input_schema.' }),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': ACC('INFERRED', {
        note: 'Expected to be accepted and rewritten to anyOf, so exclusivity is silently lost — but that behaviour is documented for GenerationConfig\'s response JSON-schema field, a different message marked deprecated:true. Unverified for this field.',
      }),
      'vertex-parameters': NO('DOC'),
      ...gbnf(SUP('SRC', { note: 'Compiled as anyOf — exclusivity is never enforced by any constrained decoder.' })),
    },
  },
  {
    keyword: 'allOf',
    gloss: 'the instance must match every subschema',
    cells: {
      'openai-strict': NO('INFERRED', {
        note: 'Absent from OpenAI\'s supported-properties list and named in the root-level error. A single-entry allOf can be inlined; a multi-entry one must be merged by hand or the tool refused.',
      }),
      'openai-nonstrict-responses': NO('INFERRED'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': NO('DOC', { providerError: ERR_ANTHROPIC_TOP_LEVEL_COMPOSITION, note: 'At the top level of input_schema.' }),
      'anthropic-strict': SUP('DOC', {
        note: 'Nested only, and `allOf` combined with `$ref` is unsupported. This is something Anthropic strict keeps that OpenAI strict loses.',
      }),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(DROP('SRC', {
        note: 'Partial: `properties`, `enum` and `$ref` components are shallow-merged; ANY other component is silently discarded (the source carries a literal "// todo warning").',
      })),
    },
  },
  ...(['not', 'if', 'then', 'else', 'dependentRequired', 'dependentSchemas'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss:
        kw === 'not'
          ? 'the instance must NOT match this subschema'
          : kw === 'dependentRequired' || kw === 'dependentSchemas'
            ? 'conditional requirements driven by which properties are present'
            : 'conditional application of a subschema',
      cells: {
        'openai-strict': NO('INFERRED', {
          note: 'Absent from OpenAI\'s supported-properties list; rejected through the family-3 "is not permitted" error. `not` is additionally named in the root-level family-4 error.',
        }),
        'openai-nonstrict-responses': NO('INFERRED'),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ANT_SILENT(),
        'anthropic-strict': ANT_SILENT(),
        'gemini-parameters': NO('DOC', { note: 'Not a field in the Schema message — 400 `Unknown name`.' }),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': NO('DOC'),
        ...gbnf(DROP('SRC')),
      },
    }),
  ),
  {
    keyword: 'const',
    gloss: 'the instance must equal exactly this value',
    cells: {
      'openai-strict': SUP('DOC', { note: 'Its serialized value counts toward the 120,000-character limit.' }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC', { note: 'Capitalisation of the generated value is NOT guaranteed — check case-insensitively after parsing.' }),
      'gemini-parameters': NO('FIELD', { note: 'Not a field in the Schema message. Rewrite to a one-element enum.' }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: 'enum',
    gloss: 'the instance must be one of these values',
    cells: {
      'openai-strict': SUP('DOC', {
        note: 'Members keep their type. Counts toward the 1000-enum-value limit, and toward the 15,000-character limit once a single string enum exceeds 250 values.',
      }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC', {
        note: 'Strings, numbers, booleans and nulls only — complex types within enums are unsupported. Capitalisation is NOT guaranteed.',
      }),
      'gemini-parameters': SUP('DOC', {
        note: 'STRINGS ONLY. Enum-ness rides on `format:"enum"`, and integer members must be quoted: {type:INTEGER, format:"enum", enum:["101","201"]}. They come back as strings — a TYPE change, not a formatting change.',
      }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC', { note: 'Same string-only handling as the Gemini API.' }),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: 'default',
    gloss: 'the value to assume when the property is absent',
    cells: {
      'openai-strict': NO('DOC', { providerError: ERR_OPENAI_DEFAULT }),
      'openai-nonstrict-responses': NO('DOC', { providerError: ERR_OPENAI_DEFAULT }),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': SUP('DOC', { note: 'Supported "for all supported types" — something Anthropic keeps that OpenAI rejects outright.' }),
      'gemini-parameters': ACC('DOC', { note: 'Documented as accepted and ignored. The field exists; it changes nothing.' }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': ACC('DOC'),
      ...gbnf(DROP('SRC')),
    },
  },
  {
    keyword: 'pattern',
    gloss: 'a regular expression the string must match',
    cells: {
      'openai-strict': SUP('DOC', {
        note: 'On the supported list since the 2025-05-20 changelog — but the regex DIALECT is undocumented. The same docs site says sampling runs under LLGuidance with Rust regex-crate syntax, which has no backreferences and no lookaround, so a browser `new RegExp()` check will green-light constructs that plausibly fail server-side.',
      }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': SUP('DOC', {
        note: 'Supported with a documented regex sub-subset. Lint the CONTENT: backreferences, lookahead/lookbehind, \\b/\\B and complex {n,m} ranges are all unsupported. "Simple regex patterns work well. Complex patterns may result in 400 errors."',
      }),
      'gemini-parameters': SUP('DOC', {
        note: 'The field exists in the Schema message. Whether Gemini enforces it is undocumented.',
      }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC', { note: 'The field exists; enforcement undocumented.' }),
      ...gbnf(SUP('SRC', {
        note: 'Compiled into the grammar ONLY if anchored `^…$`; an unanchored pattern is a HARD ERROR. Lookaround is skipped with a stderr-only warning that vanishes in a browser unless surfaced.',
      })),
    },
  },
  {
    keyword: 'format',
    gloss: 'a semantic format annotation for a string',
    cells: {
      'openai-strict': SUP('DOC', {
        note: 'Exactly nine values: date-time, time, date, duration, email, hostname, ipv4, ipv6, uuid. Whether an out-of-list value (uri, byte, int64) errors or is ignored is not established.',
      }),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': SUP('DOC', { note: 'Ten values — OpenAI\'s nine plus `uri`.' }),
      'anthropic-sdk': SUP('DOC', { note: 'The SDK filters string formats to the supported list before sending; an out-of-list format is dropped for you.' }),
      'gemini-parameters': ACC('DOC', {
        note: '"Any value is allowed, but most do not trigger any special functionality." Accepted-but-inert.',
      }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': ACC('DOC'),
      ...gbnf(SUP('SRC', {
        note: 'Only `date`, `time`, `date-time` and `uuid` (matched as ^uuid[1-5]?$) constrain anything; every other value drops to a bare string.',
      })),
    },
  },
  ...(['minLength', 'maxLength'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss: kw === 'minLength' ? 'minimum string length' : 'maximum string length',
      cells: {
        'openai-strict': UNK({
          note: 'Absent from OpenAI\'s "Supported string properties" whitelist (only `pattern` and `format` appear), and the only capture of a rejection comes from a machine-authored pull request whose own strip-list includes nine keywords OpenAI documents as supported. Warn — do not silently strip.',
        }),
        'openai-nonstrict-responses': UNK(),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ACC('DOC'),
        'anthropic-strict': NO('DOC', { note: 'String constraints are on Anthropic\'s seven-bullet unsupported list.' }),
        'anthropic-sdk': DROP('DOC', {
          note: 'The SDK removes it from the wire, appends the constraint to the description as prose, and re-validates the response against your original schema. On the wire the constraint degrades to a prompt hint — the genuine documented accept-then-drop of this whole problem space.',
        }),
        'gemini-parameters': SUP('DOC', {
          note: 'Declared format:int64, type:string. Emit a plain number (ProtoJSON accepts either), expect a string back, and treat "2" and 2 as equal when diffing.',
        }),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': SUP('DOC'),
        ...gbnf(SUP('SRC', {
          note: 'Enforced ONLY when `type` is exactly "string" AND the node has no sibling `pattern`, `enum`, `const` or supported `format`; otherwise silently DROPPED.',
        })),
      },
    }),
  ),
  ...(['minimum', 'maximum'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss: kw === 'minimum' ? 'inclusive lower numeric bound' : 'inclusive upper numeric bound',
      cells: {
        'openai-strict': SUP('DOC', { note: 'On the supported list since the 2025-05-20 changelog.' }),
        'openai-nonstrict-responses': SUP('DOC'),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ACC('DOC'),
        'anthropic-strict': NO('DOC', { note: 'Numerical constraints are on Anthropic\'s seven-bullet unsupported list.' }),
        'anthropic-sdk': DROP('DOC', {
          note: 'Stripped from the wire and appended to the description, then re-checked client-side against your original schema.',
        }),
        'gemini-parameters': SUP('DOC', { note: 'Declared as a double.' }),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': SUP('DOC'),
        ...gbnf(SUP('SRC', {
          note: 'Enforced ONLY when `type` is exactly "integer"; on "number" or an untyped node it is DROPPED (a literal // TODO in the source). Bounds compile with a hard 16-digit cap, so minimum:0 does not admit every non-negative integer.',
        })),
      },
    }),
  ),
  ...(['exclusiveMinimum', 'exclusiveMaximum'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss: kw === 'exclusiveMinimum' ? 'exclusive lower numeric bound' : 'exclusive upper numeric bound',
      cells: {
        'openai-strict': SUP('DOC'),
        'openai-nonstrict-responses': SUP('DOC'),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ACC('DOC'),
        'anthropic-strict': NO('DOC', { note: 'A numerical constraint.' }),
        'anthropic-sdk': UNK({
          note: 'The SDK\'s documented strip list is introduced with "for example" and names only minimum, maximum, minLength and maxLength. Whether it also strips this one is undocumented.',
        }),
        'gemini-parameters': NO('DOC', { note: 'The field does not exist in the 22-field Schema message.' }),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': NO('DOC'),
        ...gbnf(SUP('SRC', { note: 'Integer only, converted to an inclusive bound ±1.' })),
      },
    }),
  ),
  {
    keyword: 'multipleOf',
    gloss: 'the number must be a multiple of this value',
    cells: {
      'openai-strict': SUP('DOC'),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': NO('DOC', { note: 'A numerical constraint.' }),
      'anthropic-sdk': DROP('DOC', { note: 'Stripped from the wire and appended to the description.' }),
      'gemini-parameters': NO('DOC', { note: 'The field does not exist in the Schema message.' }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(DROP('SRC')),
    },
  },
  {
    keyword: 'minItems',
    gloss: 'minimum array length',
    cells: {
      'openai-strict': SUP('FIELD', {
        note: 'Confirmed unflagged alongside a uniqueItems 400 on the same property — direct live confirmation that min/maxItems are accepted. Note that Azure\'s table claims the opposite.',
      }),
      'openai-nonstrict-responses': SUP('FIELD'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': SUP('DOC', { note: 'Values 0 or 1 ONLY — array constraints beyond that are unsupported.' }),
      'gemini-parameters': SUP('DOC', { note: 'Declared format:int64; emit a plain number.' }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(SUP('SRC', { note: 'DROPPED when the tuple form (prefixItems) is in play.' })),
    },
  },
  {
    keyword: 'maxItems',
    gloss: 'maximum array length',
    cells: {
      'openai-strict': SUP('FIELD'),
      'openai-nonstrict-responses': SUP('FIELD'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': NO('DOC', { note: 'Only minItems 0 or 1 survives; maxItems is an unsupported array constraint.' }),
      'anthropic-sdk': UNK({
        note: 'The SDK\'s documented strip list is introduced with "for example" and does not name maxItems, so whether it strips this for you or lets the 400 through is undocumented.',
      }),
      'gemini-parameters': SUP('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(SUP('SRC', { note: 'DROPPED when the tuple form (prefixItems) is in play.' })),
    },
  },
  {
    keyword: 'uniqueItems',
    gloss: 'array members must all differ',
    cells: {
      'openai-strict': NO('FIELD', {
        providerError: ERR_OPENAI_UNIQUE_ITEMS,
        note: 'Named dissent: one verifier supplied this human-filed 2026 capture, the other reported zero evidence in either direction. The two are not symmetric — one supplies a citation, the other reports its absence — so it ships as rejected, with FIELD provenance.',
      }),
      'openai-nonstrict-responses': NO('FIELD', { providerError: ERR_OPENAI_UNIQUE_ITEMS }),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC', { note: 'The field does not exist in the Schema message.' }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(DROP('SRC', { note: 'Zero occurrences of the keyword in the converter source.' })),
    },
  },
  {
    keyword: 'prefixItems',
    gloss: '2020-12 tuple typing — a schema per array position',
    cells: {
      'openai-strict': UNK({
        note: 'Absent from OpenAI\'s supported whitelist AND absent from Azure\'s unsupported table, with no capture either way.',
      }),
      'openai-nonstrict-responses': UNK(),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK({ note: 'Listed for the response-schema field — a different message. Unverified here.' }),
      'vertex-parameters': NO('DOC'),
      ...gbnf(SUP('SRC', {
        note: 'Honoured ONLY when `items` is absent. A 2020-12 tuple carrying both silently loses the entire tuple.',
      })),
    },
  },
  {
    keyword: 'patternProperties',
    gloss: 'schemas applied to property names matching a regex',
    cells: {
      'openai-strict': NO('INFERRED', { note: 'Absent from the supported list; named in Azure\'s unsupported table.' }),
      'openai-nonstrict-responses': NO('INFERRED'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(DROP('SRC')),
    },
  },
  {
    keyword: 'propertyNames',
    gloss: 'a schema every property NAME must satisfy',
    cells: {
      'openai-strict': NO('FIELD'),
      'openai-nonstrict-responses': NO('FIELD'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('FIELD', {
        providerError: ERR_GEMINI_PROPERTY_NAMES,
        note: 'Note the proto-JSON path: it reads `any_of` (not anyOf) and `properties[N].value` for map entries, which is why nobody can find the offending node.',
      }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(DROP('SRC')),
    },
  },
  ...(['minProperties', 'maxProperties'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss: kw === 'minProperties' ? 'minimum number of properties' : 'maximum number of properties',
      cells: {
        'openai-strict': NO('INFERRED', { note: 'Absent from the supported list; named in Azure\'s unsupported table.' }),
        'openai-nonstrict-responses': NO('INFERRED'),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ANT_SILENT(),
        'anthropic-strict': ANT_SILENT(),
        'gemini-parameters': SUP('DOC', { note: 'Declared format:int64; emit a plain number.' }),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': SUP('DOC'),
        ...gbnf(DROP('SRC')),
      },
    }),
  ),
  ...(['unevaluatedProperties', 'unevaluatedItems'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss: 'the 2020-12 annotation-dependent catch-all',
      cells: {
        'openai-strict': NO('INFERRED', { note: 'Absent from the supported list; named in Azure\'s unsupported table.' }),
        'openai-nonstrict-responses': NO('INFERRED'),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ANT_SILENT(),
        'anthropic-strict': ANT_SILENT(),
        'gemini-parameters': NO('DOC'),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': NO('DOC'),
        ...gbnf(DROP('SRC')),
      },
    }),
  ),
  ...(['contains', 'minContains', 'maxContains'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss: 'at least (or at most) N array members must match a subschema',
      cells: {
        'openai-strict': NO('INFERRED', { note: 'Absent from the supported list; named in Azure\'s unsupported table.' }),
        'openai-nonstrict-responses': NO('INFERRED'),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ANT_SILENT(),
        'anthropic-strict': ANT_SILENT(),
        'gemini-parameters': NO('DOC'),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': NO('DOC'),
        ...gbnf(DROP('SRC')),
      },
    }),
  ),
  {
    keyword: 'items',
    gloss: 'the schema every array member must satisfy',
    cells: {
      'openai-strict': SUP('DOC'),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC'),
      'gemini-parameters': SUP('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(SUP('SRC')),
    },
  },
  {
    keyword: 'nullable',
    gloss: 'the OpenAPI nullability flag — not a JSON Schema keyword',
    cells: {
      'openai-strict': NO('INFERRED', { note: 'An OpenAPI-ism. Use the ["T","null"] type union instead.' }),
      'openai-nonstrict-responses': NO('INFERRED'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': SUP('DOC', {
        note: 'A first-class boolean field here — and the only way to express an optional-nullable field on this target.',
      }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(DROP('SRC')),
    },
  },
  {
    keyword: 'propertyOrdering',
    gloss: 'Gemini-only, non-standard: the order properties should be generated in',
    cells: {
      'openai-strict': NO('INFERRED'),
      'openai-nonstrict-responses': NO('INFERRED'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': UNK({
        note: 'Named on neither of Anthropic\'s lists. It is moot in practice: under strict, Anthropic reorders object keys itself — "required properties appear first, followed by optional properties" — regardless of your schema order, so a propertyOrdering carried over from Gemini is not honoured.',
      }),
      'gemini-parameters': SUP('DOC'),
      'gemini-jsonschema': SUP('DOC', { note: 'Appears in the field\'s own documentation example.' }),
      'vertex-parameters': SUP('DOC'),
    },
  },
  {
    keyword: '$schema',
    gloss: 'the dialect declaration at the root of a schema document',
    cells: {
      'openai-strict': UNK(),
      'openai-nonstrict-responses': UNK(),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ANT_SILENT(),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': NO('DOC', { providerError: ERR_GEMINI_SCHEMA_KEYWORD }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': NO('DOC'),
      ...gbnf(DROP('SRC')),
    },
  },
  {
    keyword: 'title',
    gloss: 'a human-readable name for the node',
    cells: {
      'openai-strict': UNK(),
      'openai-nonstrict-responses': UNK(),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': ACC('DOC'),
      'anthropic-strict': ANT_SILENT(),
      'gemini-parameters': SUP('DOC', { note: 'One of only two annotation fields Gemini accepts — the other is the OpenAPI singular `example`.' }),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(DROP('SRC')),
    },
  },
  ...(['examples', 'readOnly', 'writeOnly', 'deprecated', '$comment', '$id', '$anchor'] as const).map(
    (kw): KeywordRule => ({
      keyword: kw,
      gloss: 'an annotation keyword — carries no constraint',
      cells: {
        'openai-strict': UNK(),
        'openai-nonstrict-responses': UNK(),
        'openai-nonstrict-chat': CHAT,
        'anthropic-nonstrict': ACC('DOC'),
        'anthropic-strict': ANT_SILENT(),
        'gemini-parameters': NO(
          'DOC',
          kw === 'examples'
            ? {
                note: 'Gemini\'s field is the OpenAPI singular `example`. JSON Schema\'s plural `examples` is not a field in the Schema message and 400s as `Unknown name`.',
              }
            : {},
        ),
        'gemini-jsonschema': UNK(),
        'vertex-parameters': NO('DOC'),
        ...gbnf(DROP('SRC')),
      },
    }),
  ),
  {
    keyword: 'description',
    gloss: 'prose describing the node — the model reads it',
    cells: {
      'openai-strict': SUP('DOC'),
      'openai-nonstrict-responses': SUP('DOC'),
      'openai-nonstrict-chat': CHAT,
      'anthropic-nonstrict': SUP('DOC'),
      'anthropic-strict': SUP('DOC'),
      'anthropic-sdk': SUP('DOC', {
        note: 'The SDK APPENDS stripped constraints here as prose, so your description is not what you wrote.',
      }),
      'gemini-parameters': SUP('DOC'),
      'gemini-jsonschema': UNK(),
      'vertex-parameters': SUP('DOC'),
      ...gbnf(DROP('SRC', { note: 'No grammar can enforce prose. It is not a constraint on this target.' })),
    },
  },
];

/**
 * Fill the derived columns. `anthropic-sdk` = `anthropic-strict` plus the SDK's client-side rewrites,
 * so any row that does not state the SDK column explicitly inherits the strict one. `gbnf-xgrammar`
 * inherits `gbnf-llamacpp` because the two differ only in emission (§4), never in keyword handling.
 */
function finalise(rows: readonly KeywordRule[]): readonly KeywordRule[] {
  return rows.map((row) => {
    const cells: Partial<Record<TargetId, Cell>> = { ...row.cells };
    const strict = cells['anthropic-strict'];
    if (!cells['anthropic-sdk'] && strict) cells['anthropic-sdk'] = strict;
    const llama = cells['gbnf-llamacpp'];
    if (!cells['gbnf-xgrammar'] && llama) cells['gbnf-xgrammar'] = llama;
    return { ...row, cells };
  });
}

export const KEYWORD_RULES: readonly KeywordRule[] = finalise(ROWS);

const BY_KEYWORD: ReadonlyMap<string, KeywordRule> = new Map(KEYWORD_RULES.map((r) => [r.keyword, r]));

/** Exact-match lookup. Variant rows carry a `#` suffix and are never hit by a plain keyword walk. */
export function ruleFor(keyword: string): KeywordRule | undefined {
  return BY_KEYWORD.get(keyword);
}

/**
 * The cell for one keyword on one target, or `undefined` when the matrix has nothing to say.
 * `undefined` is NOT "supported" — the caller must render it as "not covered", never as a pass.
 */
export function cellFor(keyword: string, target: TargetId): Cell | undefined {
  return BY_KEYWORD.get(keyword)?.cells[target];
}

/** Every keyword the matrix covers, in matrix order. */
export const MATRIX_KEYWORDS: readonly string[] = KEYWORD_RULES.map((r) => r.keyword);

/**
 * Maps a matrix row to the field name Google's discovery `Schema` message uses, where the two differ.
 * `tests/targets.test.ts` uses this to check the hand-written Gemini/Vertex columns against Google's
 * own key list — the one part of this matrix that is mechanically verifiable.
 */
export const GEMINI_WIRE_KEY: Readonly<Record<string, string>> = {
  $defs: 'defs',
  $ref: 'ref',
};
